export interface EncryptedData {
    data: string
    iv: string
    salt: string
}

export interface CryptoConfig {
    algorithm: string
    keyLength: number
    ivLength: number
    saltLength: number
    iterations: number
}

const CRYPTO_CONFIG: CryptoConfig = {
    algorithm: 'AES-GCM',
    keyLength: 256,
    ivLength: 12,
    saltLength: 16,
    iterations: 100000,
}

class SecureCryptoManager {
    private cryptoKey: CryptoKey | null = null
    private password: string | null | undefined = null

    async initializeWithPassword(password?: string): Promise<void> {
        this.password = password
        // Clear any existing key when the password changes
        this.cryptoKey = null
    }

    private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
        const encoder = new TextEncoder()
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            encoder.encode(password),
            'PBKDF2',
            false,
            ['deriveBits', 'deriveKey']
        )

        return crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt,
                iterations: CRYPTO_CONFIG.iterations,
                hash: 'SHA-256',
            },
            keyMaterial,
            { name: CRYPTO_CONFIG.algorithm, length: CRYPTO_CONFIG.keyLength },
            false,
            ['encrypt', 'decrypt']
        )
    }

    arrayBufferToBase64(buffer: ArrayBuffer): string {
        const uint8Array = new Uint8Array(buffer)
        // Process in chunks to avoid stack overflow
        const chunkSize = 8192
        let binaryString = ''

        for (let i = 0; i < uint8Array.length; i += chunkSize) {
            const chunk = uint8Array.subarray(i, i + chunkSize)
            binaryString += String.fromCharCode.apply(null, Array.from(chunk))
        }

        return btoa(binaryString)
    }

    base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = atob(base64)
        const uint8Array = new Uint8Array(binaryString.length)

        for (let i = 0; i < binaryString.length; i++) {
            uint8Array[i] = binaryString.charCodeAt(i)
        }

        return uint8Array.buffer
    }

    async encrypt(data: string): Promise<EncryptedData> {
        if (!this.password) {
            throw new Error('Crypto manager not initialized with password')
        }

        const encoder = new TextEncoder()
        const salt = crypto.getRandomValues(new Uint8Array(CRYPTO_CONFIG.saltLength))
        const iv = crypto.getRandomValues(new Uint8Array(CRYPTO_CONFIG.ivLength))

        const key = await this.deriveKey(this.password, salt)

        const encrypted = await crypto.subtle.encrypt(
            {
                name: CRYPTO_CONFIG.algorithm,
                iv,
            },
            key,
            encoder.encode(data)
        )

        return {
            data: this.arrayBufferToBase64(encrypted),
            iv: this.arrayBufferToBase64(iv.buffer),
            salt: this.arrayBufferToBase64(salt.buffer),
        }
    }

    async decrypt(encryptedData: EncryptedData): Promise<string> {
        if (!this.password) {
            throw new Error('Crypto manager not initialized with password')
        }

        const salt = new Uint8Array(this.base64ToArrayBuffer(encryptedData.salt))
        const iv = this.base64ToArrayBuffer(encryptedData.iv)
        const data = this.base64ToArrayBuffer(encryptedData.data)

        const key = await this.deriveKey(this.password, salt)

        const decrypted = await crypto.subtle.decrypt(
            {
                name: CRYPTO_CONFIG.algorithm,
                iv,
            },
            key,
            data
        )

        return new TextDecoder().decode(decrypted)
    }

    clearPassword(): void {
        this.password = null
        this.cryptoKey = null
    }
}

export const cryptoManager = new SecureCryptoManager()
